import { useContext, useEffect, useRef } from 'react';

import {
  RolesEditorActions,
  RolesEditorSummary,
  RolesSaveBarContext,
} from '@vyce/core/src/contexts/rolesSaveBarProvider';

interface Props extends RolesEditorSummary, RolesEditorActions {}

/**
 * Registers one permission editor with the page's Save bar, when there is one.
 * Returns whether a page bar took the job — an editor without one keeps rendering
 * its own inline Save/Discard.
 *
 * Always called, never conditionally: the editor returns early for loading and
 * read-only states, so this has to sit above those returns.
 */
export const useRegisterRolesEditor = ({
  id,
  label,
  isDirty,
  canEdit,
  saving,
  invalidReason,
  needsExceptionsConfirm,
  save,
  discard,
}: Props): boolean => {
  const store = useContext(RolesSaveBarContext);
  // save/discard close over the current draft and get a new identity every render,
  // so they travel by ref — putting them in the summary would notify the bar on
  // every keystroke.
  const actionsRef = useRef<RolesEditorActions>({ save, discard });
  actionsRef.current = { save, discard };

  const summaryRef = useRef<RolesEditorSummary>({
    id,
    label,
    isDirty,
    canEdit,
    saving,
    invalidReason,
    needsExceptionsConfirm,
  });
  summaryRef.current = { id, label, isDirty, canEdit, saving, invalidReason, needsExceptionsConfirm };

  // Register once per id. Unmounting a row (a search that replaces the list, a
  // company switch) drops the entry, which is what keeps the bar honest.
  useEffect(() => {
    if (!store) return;
    return store.register(summaryRef.current, actionsRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, id]);

  useEffect(() => {
    store?.publish(summaryRef.current);
  }, [store, id, label, isDirty, canEdit, saving, invalidReason, needsExceptionsConfirm]);

  return !!store;
};
